import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Waypoint } from 'src/app/shared/components/scroll';

import { PageHeaderToolbar } from '../../../PageHeader/components/PageHeaderToolbar';
import { usePageHeader } from '../../../PageHeader/hooks/usePageHeader';

type InlinePageHeaderProps = {
	onVisibilityChange: (visible: boolean) => void;
};

export const InlinePageHeader: React.FC<InlinePageHeaderProps> = ({ onVisibilityChange }) => {
	const {
		data: { actionButtons, imageUrl, title },
	} = usePageHeader();

	const textRef = useRef<HTMLHeadingElement>(null);
	const [isMultiLine, setIsMultiLine] = useState(false);

	useEffect(() => {
		const textElement = textRef.current;
		if (!textElement) return undefined;

		const getRenderedLineCount = (element: HTMLElement) => {
			const range = document.createRange();
			range.selectNodeContents(element);
			return range.getClientRects().length;
		};

		const checkLineCount = () => {
			const lineCount = getRenderedLineCount(textElement);
			setIsMultiLine(lineCount >= 3);
		};

		// Check initially
		checkLineCount();

		// Re-check on resize
		const resizeObserver = new ResizeObserver(checkLineCount);
		resizeObserver.observe(textElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [title]);

	return (
		<section
			className="border-stroke-weaker bg-background-base hidden w-full border-b p-12 lg:block"
			data-testid="inline-page-header"
			aria-label="Starter Dashboard inline page header"
		>
			<Waypoint onEnter={() => onVisibilityChange(true)} onLeave={() => onVisibilityChange(false)} />
			<div
				data-testid="inline-page-header-content"
				className={cn('flex w-full flex-row justify-between gap-8', isMultiLine ? 'items-start' : 'items-center')}
			>
				{imageUrl && (
					<Image
						alt={title}
						data-testid="inline-header-image"
						data-qa="inline-header-image"
						src={imageUrl}
						className="flex aspect-square h-36 w-36 shrink-0 items-center justify-center gap-3.5 rounded-xl object-cover"
					/>
				)}
				<div
					data-testid="page-inline-header-title"
					className="flex w-full shrink-0 grow basis-0 flex-col items-start gap-5"
				>
					<Text
						data-testid="page-header-text"
						as="h1"
						truncate
						ref={textRef}
						className="color-[var(--typography-strong-color,#403A49)] text-size-heading-xl-sm line-clamp-3! whitespace-break-spaces!"
					>
						{title}
					</Text>
					{actionButtons.length > 0 && (
						<PageHeaderToolbar actionButtons={actionButtons} mobile={false} buttonVariant="inline" />
					)}
				</div>
			</div>
		</section>
	);
};
